import {
  DirectionsRenderer,
  GoogleMap,
  InfoWindow,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api";
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { Property } from "../../models/property.model";
import "./PropertyMap.css";

const defaultCenter = {
  lat: 11.0168, // Coimbatore latitude
  lng: 76.9558, // Coimbatore longitude
};

interface PropertyMapProps {
  properties: Property[];
  loading: boolean;
  selectedPropertyId?: string | number | null;
  routeRequest?: { origin: string; destination: string } | null;
}
import { useNavigate } from "react-router-dom";

const PropertyMap: React.FC<PropertyMapProps> = ({
  properties,
  loading,
  selectedPropertyId,
  routeRequest,
}) => {
  const navigate = useNavigate();
  const [coordinates, setCoordinates] = useState<
    { lat: number; lng: number }[]
  >([]);
  const [selectedMarkerIndex, setSelectedMarkerIndex] = useState<number | null>(
    null
  );
  const [directions, setDirections] =
    useState<google.maps.DirectionsResult | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyBFHkJCSUyW7FqPZAHPD0NpNdPWMmDcIa8",
    libraries: ["places"],
  });

  const geocodeAddresses = useCallback((addresses: string[]) => {
    // Wait for Google Maps API to be loaded and use the correct constructor
    if (
      !(
        window.google &&
        window.google.maps &&
        typeof window.google.maps.Geocoder === "function"
      )
    ) {
      console.error(
        "Google Maps API is not loaded or Geocoder is not available."
      );
      return;
    }
    const geocoder = new window.google.maps.Geocoder();
    const promises = addresses.map((address) => {
      return new Promise<{ lat: number; lng: number }>((resolve) => {
        geocoder.geocode({ address }, (results, status) => {
          if (status === "OK" && results && results[0]) {
            const { lat, lng } = results[0].geometry.location;
            resolve({ lat: lat(), lng: lng() });
          } else {
            console.warn("Geocode failed:", address, status);
            resolve(defaultCenter);
          }
        });
      });
    });
    Promise.all(promises).then(setCoordinates);
  }, []);

  // Effect to geocode property addresses when properties change
  useEffect(() => {
    if (
      !loading &&
      properties.length > 0 &&
      window.google &&
      window.google.maps
    ) {
      const validAddresses = properties
        .map((p: any) => p.address)
        .filter(Boolean) as string[];
      if (validAddresses.length > 0) {
        console.log("PropertyMap geocoding addresses:", validAddresses);
        geocodeAddresses(validAddresses);
      }
    }
  }, [properties, loading, geocodeAddresses]);

  // Effect to auto-select marker when selectedPropertyId changes
  useEffect(() => {
    if (selectedPropertyId && properties.length > 0) {
      const propertyIndex = properties.findIndex(
        (p) => String(p.id) === String(selectedPropertyId)
      );
      if (propertyIndex !== -1) {
        setSelectedMarkerIndex(propertyIndex);
      }
    }
  }, [selectedPropertyId, properties]);

  // Calculate map center - use selected property if available, otherwise use first coordinate
  const getMapCenter = useCallback(() => {
    if (selectedPropertyId && properties.length > 0 && coordinates.length > 0) {
      const propertyIndex = properties.findIndex(
        (p) => String(p.id) === String(selectedPropertyId)
      );
      if (propertyIndex !== -1 && coordinates[propertyIndex]) {
        return coordinates[propertyIndex];
      }
    }
    return coordinates[0] || defaultCenter;
  }, [selectedPropertyId, properties, coordinates]);

  // Effect to generate and display route when requested
  useEffect(() => {
    if (!routeRequest || !isLoaded || !(window.google && window.google.maps)) {
      setDirections(null);
      return;
    }

    try {
      const service = new window.google.maps.DirectionsService();
      service.route(
        {
          origin: routeRequest.origin,
          destination: routeRequest.destination,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK && result) {
            setDirections(result);
            // Fit bounds to the route
            const bounds = result.routes?.[0]?.bounds;
            if (bounds && mapRef.current) {
              mapRef.current.fitBounds(bounds);
            }
          } else {
            console.warn("Directions request failed:", status);
            setDirections(null);
          }
        }
      );
    } catch (e) {
      console.error("Error requesting directions:", e);
      setDirections(null);
    }
  }, [routeRequest, isLoaded]);

  // Resolve a property's main image URL (robust across data shapes)
  const getPropertyMainImageUrl = useCallback((property: any): string => {
    if (!property) return "/Images/no-image-placeholder.png";

    const toUrl = (raw: any) => {
      if (!raw) return undefined;
      const s = String(raw).trim();
      if (!s) return undefined;
      if (/^https?:\/\//i.test(s) || s.startsWith("/")) return s;
      if (
        /^[\w\-]+\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(s) ||
        s.indexOf("/") === -1
      )
        return `/Images/${s}`;
      return s;
    };

    const pickFromObjects = (arr: any[]): string | undefined => {
      if (!Array.isArray(arr) || arr.length === 0) return undefined;
      const getUrl = (pi: any) =>
        toUrl(
          pi &&
            (pi.imageUrl ||
              pi.ImageUrl ||
              pi.url ||
              pi.Url ||
              pi.src ||
              pi.path ||
              pi.fileName ||
              pi.FileName ||
              pi.filePath ||
              pi.FilePath)
        );
      const main = arr.find(
        (pi: any) => pi && (pi.isMainImage || pi.isMain || pi.IsMainImage)
      );
      if (main) {
        const u = getUrl(main);
        if (u) return u;
      }
      for (const it of arr) {
        const u = getUrl(it);
        if (u) return u;
      }
      return undefined;
    };

    // propertyImages could be array of strings or objects
    const pis = property.propertyImages;
    if (Array.isArray(pis) && pis.length) {
      if (typeof pis[0] === "string") {
        const u = toUrl(pis[0]);
        if (u) return u;
      } else {
        const u = pickFromObjects(pis);
        if (u) return u;
      }
    }

    // imageDtos / ImageDtos
    const idArr = property.imageDtos || property.ImageDtos;
    if (Array.isArray(idArr)) {
      const u = pickFromObjects(idArr);
      if (u) return u;
    }

    // images (strings)
    if (Array.isArray(property.images) && property.images.length) {
      const u = toUrl(property.images[0]);
      if (u) return u;
    }

    // single imageUrl field
    const single = toUrl(property.imageUrl);
    if (single) return single;

    return "/Images/no-image-placeholder.png";
  }, []);

  return (
    <div className="property-map-root">
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={{
            width: "100%",
            height: "100%",
            borderRadius: "12px",
          }}
          center={getMapCenter()}
          zoom={selectedPropertyId ? 15 : 13}
          options={{
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
          }}
          onLoad={(map) => {
            mapRef.current = map;
          }}
        >
          {coordinates.map((coord, index) => {
            const isSelected =
              selectedPropertyId &&
              String(properties[index]?.id) === String(selectedPropertyId);
            return (
              <Marker
                key={index}
                position={coord}
                onClick={() => setSelectedMarkerIndex(index)}
                icon={
                  isSelected
                    ? {
                        url:
                          "data:image/svg+xml;charset=UTF-8," +
                          encodeURIComponent(`
                    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="20" cy="20" r="18" fill="#ff6b6b" stroke="#fff" stroke-width="3"/>
                      <circle cx="20" cy="20" r="8" fill="#fff"/>
                    </svg>
                  `),
                        scaledSize: new window.google.maps.Size(40, 40),
                        anchor: new window.google.maps.Point(20, 20),
                      }
                    : undefined
                }
              />
            );
          })}

          {/* Render directions if available */}
          {directions && (
            <DirectionsRenderer
              directions={directions}
              options={{ suppressMarkers: false }}
            />
          )}

          {selectedMarkerIndex !== null &&
            coordinates[selectedMarkerIndex] &&
            properties[selectedMarkerIndex] && (
              <InfoWindow
                position={coordinates[selectedMarkerIndex]}
                onCloseClick={() => setSelectedMarkerIndex(null)}
              >
                <div className="map-info-window p-2">
                  <div className="map-info-header">
                    <img
                      className="map-info-thumb"
                      src={getPropertyMainImageUrl(
                        properties[selectedMarkerIndex]
                      )}
                      alt={properties[selectedMarkerIndex].name}
                       style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const property = properties[selectedMarkerIndex];
                        navigate(`/property/${property.name}-${property.id}`, {
                          state: {
                            propertyId: property.id,
                            checkIn: '',
                            checkOut: '',
                            roomTypeId: 0,
                          }
                        });
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "/Images/no-image-placeholder.png";
                      }}
                    />
                  </div>
                  <div className="map-info-body">
                    <h6 className="map-info-title mb-1 fw-bold">
                      {properties[selectedMarkerIndex].name}
                    </h6>
                    <small className="map-info-subtitle text-muted d-block">
                      {properties[selectedMarkerIndex].propertyType?.name ||
                        "Property"}{" "}
                      •{" "}
                      {properties[selectedMarkerIndex].location?.name ||
                        properties[selectedMarkerIndex].city?.name}
                    </small>
                    <small className="map-info-address text-muted d-block">
                      {properties[selectedMarkerIndex].address}
                    </small>
                  </div>
                </div>
              </InfoWindow>
            )}
        </GoogleMap>
      ) : (
        <div className="d-flex align-items-center justify-content-center h-100">
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading map...</span>
            </div>
            <p className="text-muted">Loading map...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyMap;
